import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ProductProvider } from "./context/ProductContext";
import RouteGuard from "./components/RouteGuard";
import Layout from "./components/Layout";

import Login from "./pages/Login";
import Landing from "./pages/Landing";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import CRM from "./pages/CRM";
import Leads from "./pages/Leads";
import Opportunities from "./pages/Opportunities";
import Conversations from "./pages/Conversations";
import Calendar from "./pages/Calendar";
import Team from "./pages/Team";
import RolesPermissions from "./pages/RolesPermissions";
import Settings from "./pages/Settings";
import GenericModule from "./pages/GenericModule";
import RecordDetail from "./pages/RecordDetail";
import CadenceTrackerPage from "./pages/CadenceTrackerPage";
import LeadConversationPage from "./pages/LeadConversationPage";
import Directory from "./pages/Directory";
import DirectoryDetail from "./pages/DirectoryDetail";
import PartnerContacts from "./pages/PartnerContacts";
import Documents from "./pages/Documents";
import Contracts from "./pages/Contracts";
import ContractFill from "./pages/ContractFill";
import CoverLetterFill from "./pages/CoverLetterFill";
import LetterOfIntentFill from "./pages/LetterOfIntentFill";
import InvestorPortal from "./pages/InvestorPortal";
import ContactRequests from "./pages/ContactRequests";
import IntegrationsAdmin from "./pages/IntegrationsAdmin";
import IntegrationMapping from "./pages/IntegrationMapping";
import LeadImportTemplate from "./pages/LeadImportTemplate";
import CommunicationPage from "./pages/CommunicationPage";
import PropertyIntelligence from "./pages/PropertyIntelligence";
import Reports from "./pages/Reports";
import AIOffice from "./pages/AIOffice";
import AgentCommandCenter from "./pages/AgentCommandCenter";
import DataSources from "./pages/DataSources";
import AIExecutions from "./pages/AIExecutions";
import DailyBrief from "./pages/DailyBrief";
import AcqOutreach from "./pages/AcqOutreach";
import AcqLearning from "./pages/AcqLearning";
import AcquisitionPipeline from "./pages/AcquisitionPipeline";
import AcquisitionDeal from "./pages/AcquisitionDeal";
import Calculations from "./pages/Calculations";
import MyBuyBoxes from "./pages/MyBuyBoxes";
import BuilderBuyBox from "./pages/BuilderBuyBox";
import BuilderBuyBoxEditor from "./pages/BuilderBuyBoxEditor";
import AIBuyBoxes from "./pages/AIBuyBoxes";
import PropertyCandidates from "./pages/PropertyCandidates";
import AIBuyBoxEditor from "./pages/AIBuyBoxEditor";
import CommsAgents from "./pages/CommsAgents";
import AIOperations from "./pages/AIOperations";
import AIRunDetail from "./pages/AIRunDetail";
import BuyBoxEditor from "./pages/BuyBoxEditor";
import BuyBoxSubmissions from "./pages/BuyBoxSubmissions";
import PublicBuyBox from "./pages/PublicBuyBox";
import ConceptSitePlanPage from "./components/ConceptSitePlan";
import Deal2060 from "./pages/Deal2060";
import MailerPage from "./pages/MailerPage";
import SellYourLandPublic from "./pages/SellYourLandPublic";
import SellYourLandAdmin from "./pages/SellYourLandAdmin";
import MailTemplates from "./pages/MailTemplates";
import Decks from "./pages/Decks";
import DeckBuilder, { DeckPreview } from "./pages/DeckBuilder";
import PublicDeck from "./pages/PublicDeck";
import ProjectDetail from "./pages/ProjectDetail";

const app = (page, menuKey, adminOnly) => (
  <RouteGuard menuKey={menuKey} adminOnly={adminOnly}>
    <Layout>{page}</Layout>
  </RouteGuard>
);

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <ProductProvider>
            <BrowserRouter>
              <Toaster position="top-right" richColors />
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/" element={<Landing />} />
                <Route path="/about" element={<Landing page="about" />} />
                <Route path="/developments" element={<Landing page="developments" />} />
                <Route path="/process" element={<Landing page="approach" />} />
                <Route path="/teknology" element={<Landing page="teknology" />} />
                <Route path="/partners" element={<Landing page="investment" />} />
                <Route path="/investment" element={<Navigate to="/partners" replace />} />
                <Route path="/developments/:slug" element={<ProjectDetail />} />
                <Route path="/deal/:token" element={<InvestorPortal />} />
                <Route path="/buybox/:slug" element={<PublicBuyBox />} />
                <Route path="/sell-your-land" element={<SellYourLandPublic />} />
                <Route path="/app" element={app(<Dashboard />, "/")} />
                <Route path="/crm" element={app(<CRM />, "/leads")} />
                <Route path="/reports" element={app(<Reports />, "/")} />
                <Route path="/ai-office" element={app(<AIOffice />, "/")} />
                <Route path="/ai-office/agents" element={app(<AgentCommandCenter />, "/")} />
                <Route path="/ai-office/data-sources" element={app(<DataSources />, "/")} />
                <Route path="/ai-office/executions" element={app(<AIExecutions />, "/")} />
                <Route path="/ai-office/leads/:leadId/outreach" element={app(<AcqOutreach />, "/")} />
                <Route path="/ai-office/brief" element={app(<DailyBrief />, "/")} />
                <Route path="/ai-office/learning" element={app(<AcqLearning />, "/")} />
                <Route path="/ai-agents" element={<Navigate to="/ai-office" replace />} />
                <Route path="/analysis" element={app(<Leads title="Property Analysis" resource="analysis" subtitle="Underwrite deals and model returns before committing" />, "/leads")} />
                <Route path="/leads" element={app(<Leads title="Leads" resource="inbound_leads" subtitle="Capture and qualify inbound leads" />, "/leads")} />
                <Route path="/property-screening" element={app(<Leads title="Property Screening" resource="property_search" subtitle="Source and screen land opportunities" />, "/leads")} />
                <Route path="/property-intelligence" element={app(<PropertyIntelligence />, "/leads")} />
                <Route path="/projects" element={app(<Leads title="Projects" resource="projects" showOwnerFilter={false} subtitle="Track active developments through the build lifecycle" />, "/leads")} />
                <Route path="/acquisition" element={app(<AcquisitionPipeline />, "/leads")} />
                <Route path="/acquisition/:id" element={app(<AcquisitionDeal />, "/leads")} />
                <Route path="/planning" element={app(<Leads title="Planning" resource="planning" showOwnerFilter={false} subtitle="Entitlements, feasibility, and site planning" />, "/leads")} />
                <Route path="/preconstruction" element={app(<Leads title="Preconstruction" resource="preconstruction" subtitle="Estimating, bids, and permitting" />, "/leads")} />
                <Route path="/construction" element={app(<Leads title="Construction" resource="construction" subtitle="Manage the build and track field progress" />, "/leads")} />
                <Route path="/closeout" element={app(<Leads title="Closeout" resource="closeout" subtitle="Final inspections, punch list, and handover" />, "/leads")} />
                <Route path="/directory" element={app(<Directory />, "/leads")} />
                <Route path="/directory/:id" element={app(<DirectoryDetail />, "/leads")} />
                <Route path="/partner-contacts" element={app(<PartnerContacts />, "/leads")} />
                <Route path="/documents" element={app(<Documents />, "/leads")} />
                <Route path="/contracts" element={app(<Contracts />, "/leads")} />
                <Route path="/record/:resource/:id/contract" element={app(<ContractFill />, "/leads")} />
                <Route path="/record/:resource/:id/cover-letter" element={app(<CoverLetterFill />, "/leads")} />
                <Route path="/record/:resource/:id/loi" element={app(<LetterOfIntentFill />, "/leads")} />
                <Route path="/record/:resource/:id/option-loi" element={app(<LetterOfIntentFill formPath="/contracts/option-loi/form" generatePath="/contracts/option-loi/generate" />, "/leads")} />
                <Route path="/record/:resource/:id/ground-lease-loi" element={app(<LetterOfIntentFill formPath="/contracts/ground-lease-loi/form" generatePath="/contracts/ground-lease-loi/generate" />, "/leads")} />
                <Route path="/record/:resource/:id/jv-loi" element={app(<LetterOfIntentFill formPath="/contracts/jv-loi/form" generatePath="/contracts/jv-loi/generate" />, "/leads")} />
                <Route path="/schedule" element={app(<Leads title="Schedule" resource="schedule" subtitle="Coordinate milestones, trades, and timelines" />, "/leads")} />
                <Route path="/contacts" element={app(<GenericModule entityKey="contacts" subtitle="People associated with customers and opportunities" />, "/contacts")} />
                <Route path="/opportunities" element={app(<Opportunities />, "/opportunities")} />
                <Route path="/accounts" element={app(<GenericModule entityKey="accounts" subtitle="Company records — contacts, leads, opportunities, and subscription health" />, "/accounts")} />
                <Route path="/conversations" element={app(<Conversations />, "/conversations")} />
                <Route path="/calendar" element={app(<Calendar />, "/calendar")} />
                <Route path="/tasks" element={app(<GenericModule entityKey="tasks" subtitle="Follow-ups, calls, and to-dos" />, "/tasks")} />
                <Route path="/team" element={app(<Team />, "/team")} />
                <Route path="/calculations" element={app(<Calculations />, "/")} />
                <Route path="/buybox" element={app(<MyBuyBoxes />, "/")} />
                <Route path="/builder-buy-box" element={app(<BuilderBuyBox />, "/")} />
                <Route path="/builder-buy-box/new" element={app(<BuilderBuyBoxEditor />, "/")} />
                <Route path="/builder-buy-box/:id/edit" element={app(<BuilderBuyBoxEditor />, "/")} />
                <Route path="/buybox/new" element={app(<BuyBoxEditor />, "/")} />
                <Route path="/buybox/:id/edit" element={app(<BuyBoxEditor />, "/")} />
                <Route path="/buybox/:id/submissions" element={app(<BuyBoxSubmissions />, "/")} />
                <Route path="/ai-buybox" element={app(<AIBuyBoxes />, "/")} />
                <Route path="/property-candidates" element={app(<PropertyCandidates />, "/")} />
                <Route path="/ai-buybox/new" element={app(<AIBuyBoxEditor />, "/")} />
                <Route path="/ai-buybox/:id/edit" element={app(<AIBuyBoxEditor />, "/")} />
                <Route path="/ai-comms" element={app(<CommsAgents />, "/")} />
                <Route path="/ai-operations" element={app(<AIOperations />, "/")} />
                <Route path="/ai-operations/runs/:runId" element={app(<AIRunDetail />, "/")} />
                <Route path="/sell-land" element={app(<SellYourLandAdmin />, "/")} />
                <Route path="/mail-templates" element={app(<MailTemplates />, "/")} />
                <Route path="/roles" element={app(<RolesPermissions />, "/roles", true)} />
                <Route path="/settings" element={app(<Settings />, "/settings")} />
                <Route path="/contact-requests" element={app(<ContactRequests />, "/contact-requests", true)} />
                <Route path="/integrations" element={app(<IntegrationsAdmin />, "/integrations", true)} />
                <Route path="/integration-mapping" element={app(<IntegrationMapping lockProvider="PropertyRadar" />, "/integration-mapping", true)} />
                <Route path="/mapping/propertyradar" element={app(<IntegrationMapping lockProvider="PropertyRadar" />, "/mapping/propertyradar", true)} />
                <Route path="/mapping/realestateapi" element={app(<IntegrationMapping lockProvider="RealEstateAPI" />, "/mapping/realestateapi", true)} />
                <Route path="/lead-import-template" element={app(<LeadImportTemplate />, "/lead-import-template", true)} />
                <Route path="/leads/:id/cadence" element={app(<CadenceTrackerPage />, "/leads")} />
                <Route path="/leads/:id/conversation" element={app(<LeadConversationPage />, "/leads")} />
                <Route path="/record/:resource/:id/concept" element={app(<ConceptSitePlanPage />, "record")} />
                <Route path="/record/:resource/:id/2060" element={app(<Deal2060 />, "record")} />
                <Route path="/record/:resource/:id/mailer" element={app(<MailerPage />, "record")} />
                <Route path="/record/:resource/:id" element={app(<RecordDetail />, "record")} />
                <Route path="/communication/:resource/:id" element={app(<CommunicationPage />, "record")} />
                <Route path="/decks" element={app(<Decks />, "/leads")} />
                <Route path="/decks/:id" element={<RouteGuard menuKey="/leads"><DeckBuilder /></RouteGuard>} />
                <Route path="/decks/:id/preview" element={<RouteGuard menuKey="/leads"><DeckPreview /></RouteGuard>} />
                <Route path="/:slug" element={<PublicDeck />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </ProductProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
